// src/polyfills/submitter.js
var submittersByForm = /* @__PURE__ */ new WeakMap();
function findSubmitterFromClickTarget(target) {
  const element = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
  const candidate = element ? element.closest("input, button") : null;
  return candidate?.type == "submit" ? candidate : null;
}
function clickCaptured(event) {
  const submitter = findSubmitterFromClickTarget(event.target);
  if (submitter && submitter.form) {
    submittersByForm.set(submitter.form, submitter);
  }
}
(function() {
  if ("submitter" in Event.prototype)
    return;
  let prototype = window.Event.prototype;
  if ("SubmitEvent" in window && /Apple Computer/.test(navigator.vendor)) {
    prototype = window.SubmitEvent.prototype;
  } else if ("SubmitEvent" in window) {
    return;
  }
  addEventListener("click", clickCaptured, true);
  Object.defineProperty(prototype, "submitter", {
    get() {
      if (this.type == "submit" && this.target instanceof HTMLFormElement) {
        return submittersByForm.get(this.target);
      }
    }
  });
})();

// src/polyfills/request-submit.js
(function(prototype) {
  if (typeof prototype.requestSubmit == "function")
    return;
  prototype.requestSubmit = function(submitter) {
    if (submitter) {
      validateSubmitter(submitter, this);
      submitter.click();
    } else {
      submitter = document.createElement("input");
      submitter.type = "submit";
      submitter.hidden = true;
      this.appendChild(submitter);
      submitter.click();
      this.removeChild(submitter);
    }
  };
  function validateSubmitter(submitter, form) {
    submitter instanceof HTMLElement || raise(TypeError, "parameter 1 is not of type 'HTMLElement'");
    submitter.type == "submit" || raise(TypeError, "The specified element is not a submit button");
    submitter.form == form || raise(DOMException, "The specified element is not owned by this form element", "NotFoundError");
  }
  function raise(errorConstructor, message, name) {
    throw new errorConstructor("Failed to execute 'requestSubmit' on 'HTMLFormElement': " + message + ".", name);
  }
})(HTMLFormElement.prototype);

// src/send.js
var jobs = {};
async function send({ method, action, body, referrer, headers }, followRedirects) {
  let proxy;
  let handleRedirects = redirectHandler(followRedirects);
  let onSuccess = (response2) => response2;
  let onError = (error) => error;
  if (method === "GET") {
    proxy = enqueue(action);
    if (isLocked(action)) {
      return proxy;
    }
    onSuccess = (response2) => dequeue(action, (job) => job.resolve(response2));
    onError = (error) => dequeue(action, (job) => job.reject(error));
  }
  referrer = referrer || window.location.href;
  let response = fetch(action, {
    headers,
    referrer,
    method,
    body
  }).then(handleRedirects).then(readHtml).then(onSuccess).catch(onError);
  return method === "GET" ? proxy : response;
}
function readHtml(response) {
  return response.text().then((html) => {
    response.html = html;
    return response;
  });
}
function enqueue(key) {
  if (!jobs[key]) {
    jobs[key] = [];
  }
  let job = {};
  let proxy = new Promise((resolve, reject) => {
    job.resolve = resolve;
    job.reject = reject;
  });
  jobs[key].push(job);
  return proxy;
}
function isLocked(key) {
  return jobs[key].length > 1;
}
function dequeue(key, resolver) {
  (jobs[key] || []).forEach(resolver);
  jobs[key] = void 0;
}
function redirectHandler(follow) {
  return follow ? (response) => response : (response) => {
    if (response.redirected) {
      window.location.href = response.url;
      return;
    }
    return response;
  };
}

// src/index.js
var globalConfig = {
  followRedirects: true,
  headers: {},
  mergeStrategy: "replace"
};
var sendConfig = /* @__PURE__ */ new WeakMap();
var mergeConfig = /* @__PURE__ */ new WeakMap();
var morphElement = (from, to) => {
  console.error(`You can't use the "morph" merge without first installing the Alpine "morph" plugin here: https://alpinejs.dev/plugins/morph`);
};
function Ajax(Alpine) {
  if (Alpine.morph)
    morphElement = Alpine.morph;
  window.addEventListener("popstate", (e) => {
    if (!e.state || !e.state.__AJAX__)
      return;
    window.location.reload(true);
  });
  Alpine.directive("target", (el, { modifiers, expression }, { evaluate, cleanup }) => {
    let config = {
      targets: parseIds(el, expression),
      events: true,
      ...parseModifiers(modifiers)
    };
    sendConfig.set(el, config);
    config.headers = Object.assign(
      globalConfig.headers,
      evaluate(Alpine.bound(el, "x-headers", "{}"))
    );
    if (isLocalLink(el)) {
      cleanup(listenForNavigate(el, config));
    } else if (isForm(el)) {
      cleanup(listenForSubmit(el, config));
    }
  });
  Alpine.addInitSelector(() => `[${Alpine.prefixed("merge")}]`);
  Alpine.directive("merge", (el, { modifiers, expression }) => {
    mergeConfig.set(el, {
      strategy: expression,
      transition: transition(modifiers)
    });
  });
  Alpine.magic("ajax", (el) => {
    return (action, options = {}) => {
      let method = options.method ? options.method.toUpperCase() : "GET";
      let enctype = options.enctype || "application/x-www-form-urlencoded";
      let body = null;
      if (options.body) {
        body = parseFormData(options.body);
        if (method === "GET") {
          action = mergeBodyIntoAction(body, action);
          body = null;
        } else if (enctype !== "multipart/form-data") {
          body = formDataToParams(body);
        }
      }
      let headers = options.headers;
      if (!headers) {
        headers = el.hasAttribute("x-headers") ? Alpine.evaluate(el, Alpine.bound(el, "x-headers", "{}")) : {};
      }
      headers = Object.assign(globalConfig.headers, headers);
      let request = {
        action,
        method,
        body,
        headers,
        referrer: source(el)
      };
      let config = sendConfig.get(el) || {
        followRedirects: globalConfig.followRedirects,
        history: false,
        focus: false
      };
      config = Object.assign(config, options);
      let ids = parseIds(el, config.targets || config.target);
      let targets = findTargets(ids);
      targets = config.sync ? addSyncTargets(targets) : targets;
      return render(request, targets, el, config);
    };
  });
}
Ajax.configure = (options) => {
  globalConfig = Object.assign(globalConfig, options);
  return Ajax;
};
var src_default = Ajax;
function parseIds(el, expression = null) {
  let ids = [el.id];
  if (expression) {
    ids = Array.isArray(expression) ? expression : expression.split(" ");
  }
  ids = ids.filter((id) => id);
  if (ids.length === 0) {
    throw new MissingIdError(el);
  }
  return ids;
}
function parseModifiers(modifiers = []) {
  let followRedirects = globalConfig.followRedirects ? !modifiers.includes("nofollow") : modifiers.includes("follow");
  let history = false;
  if (modifiers.includes("push"))
    history = "push";
  if (modifiers.includes("replace"))
    history = "replace";
  return {
    followRedirects,
    history,
    focus: !modifiers.includes("nofocus")
  };
}
function transition(modifiers = []) {
  return globalConfig.transitions || modifiers.includes("transition");
}
function isLocalLink(el) {
  return el.href && !el.hash && el.origin == location.origin;
}
function isForm(el) {
  return el.tagName === "FORM";
}
function parseFormData(data) {
  if (data instanceof HTMLFormElement)
    return new FormData(data);
  if (data instanceof FormData)
    return data;
  const formData = new FormData();
  for (let key in data) {
    if (typeof data[key] === "object") {
      formData.append(key, JSON.stringify(data[key]));
    } else {
      formData.append(key, data[key]);
    }
  }
  return formData;
}
function listenForNavigate(el, config) {
  let handler = async (event) => {
    if (event.which > 1 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    let request = navigateRequest(el);
    request.headers = config.headers || {};
    let targets = addSyncTargets(findTargets(config.targets));
    try {
      return await render(request, targets, el, config);
    } catch (error) {
      if (error instanceof FailedResponseError) {
        console.warn(error.message);
        window.location.href = el.href;
        return;
      }
      throw error;
    }
  };
  el.addEventListener("click", handler);
  return () => el.removeEventListener("click", handler);
}
function navigateRequest(link) {
  return {
    method: "GET",
    action: link.href,
    referrer: source(link),
    body: null
  };
}
function listenForSubmit(el, config) {
  let handler = async (event) => {
    if (event.submitter && event.submitter.hasAttribute("formnoajax")) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    let request = formRequest(el, event.submitter);
    request.headers = config.headers || {};
    let targets = addSyncTargets(findTargets(config.targets));
    try {
      return await withSubmitter(event.submitter, () => {
        return render(request, targets, el, config);
      });
    } catch (error) {
      if (error instanceof FailedResponseError) {
        console.warn(error.message);
        el.removeEventListener("submit", handler);
        el.requestSubmit(event.submitter);
        return;
      }
      throw error;
    }
  };
  el.addEventListener("submit", handler);
  return () => el.removeEventListener("submit", handler);
}
function formRequest(form, submitter = null) {
  let method = (form.getAttribute("method") || "GET").toUpperCase();
  let enctype = form.getAttribute("enctype") || "application/x-www-form-urlencoded";
  let referrer = source(form);
  let action = form.getAttribute("action") || referrer || window.location.href;
  let body = parseFormData(form);
  if (submitter) {
    method = submitter.getAttribute("formmethod") || method;
    enctype = submitter.getAttribute("formenctype") || enctype;
    action = submitter.getAttribute("formaction") || action;
    if (submitter.name) {
      body.append(submitter.name, submitter.value);
    }
  }
  if (method === "GET") {
    action = mergeBodyIntoAction(body, action);
    body = null;
  } else if (enctype !== "multipart/form-data") {
    body = formDataToParams(body);
  }
  return { method, action, body, referrer };
}
async function withSubmitter(submitter, callback) {
  if (!submitter)
    return await callback();
  let disableEvent = (e) => e.preventDefault();
  submitter.setAttribute("aria-disabled", "true");
  submitter.addEventListener("click", disableEvent);
  let result = await callback();
  submitter.removeAttribute("aria-disabled");
  submitter.removeEventListener("click", disableEvent);
  return result;
}
function mergeBodyIntoAction(body, action) {
  let params = formDataToParams(body);
  if (Array.from(params).length) {
    let parts = action.split("#");
    let hash = parts[1];
    action += parts[0].includes("?") ? "&" : "?";
    action += params;
    if (hash) {
      action += "#" + hash;
    }
  }
  return action;
}
function formDataToParams(body) {
  let params = Array.from(body.entries()).filter(([key, value]) => {
    return !(value instanceof File);
  });
  return new URLSearchParams(params);
}
async function render(request, targets, el, config) {
  let dispatch = () => true;
  if (config.events) {
    dispatch = (el2, name, detail) => {
      return el2.dispatchEvent(
        new CustomEvent(name, {
          detail,
          bubbles: true,
          composed: true,
          cancelable: true
        })
      );
    };
  }
  if (!dispatch(el, "ajax:before"))
    return;
  let targetIds = [];
  targets.forEach((target) => {
    target.setAttribute("aria-busy", "true");
    targetIds.push(target.id);
  });
  request.headers["X-Alpine-Request"] = "true";
  request.headers["X-Alpine-Target"] = targetIds.join("  ");
  let response = await send(request, config.followRedirects);
  if (response.ok) {
    dispatch(el, "ajax:success", response);
  } else {
    dispatch(el, "ajax:error", response);
  }
  dispatch(el, "ajax:after", response);
  if (!response.html)
    return;
  if (config.history) {
    updateHistory(config.history, response.url);
  }
  let wrapper = document.createRange().createContextualFragment("<template>" + response.html + "</template>");
  let fragment = wrapper.firstElementChild.content;
  let focused = !config.focus;
  let renders = targets.map(async (target) => {
    let content = fragment.getElementById(target.id);
    let strategy = mergeConfig.get(target)?.strategy || globalConfig.mergeStrategy;
    if (!content) {
      if (!dispatch(el, "ajax:missing", { response, fragment })) {
        return;
      }
      if (response.ok) {
        return target.remove();
      }
      throw new FailedResponseError(el);
    }
    let mergeContent = async () => {
      let merged = await merge(strategy, target, content);
      if (merged) {
        merged.dataset.source = response.url;
        merged.removeAttribute("aria-busy");
        let focusables = ["[x-autofocus]", "[autofocus]"];
        focusables.some((selector) => {
          if (focused)
            return true;
          if (merged.matches(selector)) {
            focused = focusOn(merged);
          }
          return focused || Array.from(merged.querySelectorAll(selector)).some((focusable) => focusOn(focusable));
        });
      }
      dispatch(merged, "ajax:merged");
      return merged;
    };
    if (!dispatch(target, "ajax:merge", { strategy, content, merge: mergeContent })) {
      return;
    }
    return mergeContent();
  });
  return await Promise.all(renders);
}
async function merge(strategy, from, to) {
  let strategies = {
    before(from2, to2) {
      from2.before(...to2.childNodes);
      return from2;
    },
    replace(from2, to2) {
      from2.replaceWith(to2);
      return to2;
    },
    update(from2, to2) {
      from2.replaceChildren(...to2.childNodes);
      return from2;
    },
    prepend(from2, to2) {
      from2.prepend(...to2.childNodes);
      return from2;
    },
    append(from2, to2) {
      from2.append(...to2.childNodes);
      return from2;
    },
    after(from2, to2) {
      from2.after(...to2.childNodes);
      return from2;
    },
    morph(from2, to2) {
      morphElement(from2, to2);
      return document.getElementById(to2.id);
    }
  };
  if (!mergeConfig.get(from)?.transition || !document.startViewTransition) {
    return strategies[strategy](from, to);
  }
  let merged = null;
  let transition2 = document.startViewTransition(() => {
    merged = strategies[strategy](from, to);
    return Promise.resolve();
  });
  await transition2.updateCallbackDone;
  return merged;
}
function focusOn(el) {
  if (!el)
    return false;
  if (!el.getClientRects().length)
    return false;
  setTimeout(() => {
    if (!el.hasAttribute("tabindex"))
      el.setAttribute("tabindex", "0");
    el.focus();
  }, 0);
  return true;
}
function updateHistory(strategy, url) {
  let strategies = {
    push: () => window.history.pushState({ __AJAX__: true }, "", url),
    replace: () => window.history.replaceState({ __AJAX__: true }, "", url)
  };
  return strategies[strategy]();
}
function findTargets(ids = []) {
  return ids.map((id) => {
    let target = document.getElementById(id);
    if (!target) {
      throw new MissingTargetError(id);
    }
    return target;
  });
}
function addSyncTargets(targets) {
  document.querySelectorAll("[x-sync]").forEach((el) => {
    if (!el.id) {
      throw new MissingIdError(el);
    }
    if (!targets.some((target) => target.id === el.id)) {
      targets.push(el);
    }
  });
  return targets;
}
function source(el) {
  return el.closest("[data-source]")?.dataset.source;
}
var MissingIdError = class extends Error {
  constructor(el) {
    let description = (el.outerHTML.match(/<[^>]+>/) ?? [])[0] ?? "[Element]";
    super(`${description} is missing an ID to target.`);
    this.name = "Missing ID";
  }
};
var MissingTargetError = class extends Error {
  constructor(id) {
    super(`#${id} was not found in the current document.`);
    this.name = "Missing Target";
  }
};
var FailedResponseError = class extends Error {
  constructor(el) {
    let description = (el.outerHTML.match(/<[^>]+>/) ?? [])[0] ?? "[Element]";
    super(`${description} received a failed response.`);
    this.name = "Failed Response";
  }
};

// builds/module.js
var module_default = src_default;
export {
  module_default as default
};
